import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { CostCenterListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { client } from "@/hono";
import { EditCostCenterModal } from "@/modals/cost-centers/edit-cost-center-modal";

export function CostCentersRowActions({ cell }: CellContext<CostCenterListItem, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const qc = useQueryClient();
	const asyncModal = useAsyncModal();
	const editModal = useDisclosure();

	/** Computed */
	const costCenterId = cell.row.original.id;

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.cost_centers[":costCenterId"].$delete({
			param: { costCenterId },
		});
		const data = await responseHandler(response);
		// A cost centre that is booked to stays, deactivated; say so rather than let it look like
		// the delete did nothing.
		if (data.result === "deactivated") toast.info(t("cost_centers.deactivated_instead"));
		await qc.invalidateQueries({ predicate: invalidate(["cost-centers"]) });
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={null}>
				<Menu
					items={createStandardMenuItems({
						items: [],
						onEdit: editModal.onOpen,
						onEditOptions: { isDisabled: !!citadel && citadel.cannot("update", "Organization") },
						onDelete: handleOnDelete,
						onDeleteOptions: {
							isDisabled: !!citadel && citadel.cannot("delete", "Organization"),
						},
					})}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
					/>
				</Menu>
			</RowActionsWrapper>
			{editModal.isOpen && (
				<EditCostCenterModal
					costCenterId={costCenterId}
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t("cost_centers.delete_cost_center_confirm_message")}
					controller={asyncModal}
				/>
			)}
		</Fragment>
	);
}
